<template>
  <div class="dod-container" v-if="!loading">
    <dod-view-title :title="'Raporty'" />

    <div class="action-buttons">
      <div class="generate-reports">
        <custom-select
          :key="`reportsOptions_${allOptions.length}`"
          :options="allOptions"
          :name="'Wybierz raport'"
          :prePick="selectedReportType"
          @newPick="(type) => (selectedReportType = type)"
        />

        <dod-month-switcher
          v-if="selectedNeedsMonth"
          class="month"
          :date="date"
          @onMonthChange="onMonthChange"
        />

        <custom-button
          :customStyles="'height: 40px;padding: 0;margin:0; width: fit-content; padding: 0 1rem'"
          :size="'small'"
          :title="generating ? 'Generowanie...' : 'Wygeneruj raport'"
          :disabled="generating"
          @click="generateReport"
        />

        <custom-button
          class="ml-4"
          v-if="isSqlReportSelected"
          :customStyles="'height: 40px;padding: 0;margin:0; width: fit-content; padding: 0 1rem'"
          :size="'small'"
          :title="'Usuń raport'"
          @click="deleteReport"
        />
      </div>

      <div class="new-report">
        <custom-button
          :customStyles="'height: 40px;padding: 0;margin:0; width: fit-content; padding: 0 1rem'"
          :size="'small'"
          :title="'Utwórz nowy raport'"
          @click="newReportVisible = !newReportVisible"
        />
      </div>
    </div>

    <div v-if="newReportVisible">
      <DodGenerateSQL />
    </div>

    <pagination-arrows
      v-if="archive.meta && archive.meta.total"
      :onPage="archive.meta.per_page"
      :pageNumber="archive.meta.current_page"
      :total="archive.meta.total"
      @nextPage="handleNextPage"
      @previousPage="handlePreviousPage"
    />

    <loader v-if="loadingData" />

    <table class="table table-striped" v-else-if="archive.data && archive.data.length > 0">
      <thead>
        <tr>
          <td>Nazwa</td>
          <td>Okres raportowania</td>
          <td>Utworzone przez</td>
          <td>Data wygenerowania</td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        <tr v-for="report in archive.data" :key="report.id">
          <td>{{ report.name }}</td>
          <td>{{ periodLabel(report) }}</td>
          <td>{{ report.createdByName }}</td>
          <td>{{ formatDate(report.createdAt) }}</td>
          <td>
            <span class="cell-end">
              <gradient-button
                @handleClick="download(report)"
                :title="'Pobierz'"
                :customStyles="'width: 5vw; font-size: calc(11px + 0.05vw);'"
              />
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <dod-empty-list v-else />
  </div>
</template>

<script>
import DodViewTitle from "../../components/dod/DodViewTitle.vue";
import DodMonthSwitcher from "../../components/dod/DodMonthSwitcher.vue";
import moment from "moment";
import GradientButton from "../../components/dod/GradientButton.vue";
import DodEmptyList from "../../components/dod/DodEmptyList.vue";
import CustomSelect from "../../components/CustomSelect.vue";
import CustomButton from "../../components/CustomButton.vue";
import DodGenerateSQL from "../../components/dod/DodGenerateSQL.vue";
import PaginationArrows from "../../components/PaginationArrows.vue";
import Loader from "../../components/Loader.vue";

const DNR_BILLS = "DNR_BILLS";
const PAGE_SIZE = 20;

export default {
  components: {
    DodViewTitle,
    DodMonthSwitcher,
    GradientButton,
    DodEmptyList,
    CustomSelect,
    CustomButton,
    DodGenerateSQL,
    PaginationArrows,
    Loader,
  },
  name: "DODReportsList",
  data() {
    return {
      loading: true,
      loadingData: false,
      generating: false,
      month: "",
      year: "",
      date: "",
      selectedReportType: "W1",
      newReportVisible: false,
      page: 1,
    };
  },

  async mounted() {
    const now = moment();
    this.date = now.toDate();
    this.month = now.get("month") + 1;
    this.year = now.get("year");
    // Domyślnie pierwszy dostępny raport (w POD część raportów jest ukryta, więc nie zostawiamy "W1").
    if (this.reportsOptions && this.reportsOptions.length) {
      this.selectedReportType = this.reportsOptions[0].value;
    }
    await this.$store.dispatch("fetchSavedSqlReports");
    await this.fetchArchive();
    this.loading = false;
  },

  methods: {
    async fetchArchive() {
      try {
        await this.$store.dispatch("fetchReportArchive", {
          page: this.page,
          limit: PAGE_SIZE,
        });
      } catch (error) {
        console.log(error);
        this.$notify({ text: "Nie udało się pobrać archiwum raportów", group: "error" });
      }
    },
    async handleNextPage() {
      this.page++;
      this.loadingData = true;
      await this.fetchArchive();
      this.loadingData = false;
    },
    async handlePreviousPage() {
      this.page--;
      this.loadingData = true;
      await this.fetchArchive();
      this.loadingData = false;
    },
    onMonthChange(date) {
      const dateToSet = moment(date);
      this.date = dateToSet.toDate();
      this.month = dateToSet.get("month") + 1;
      this.year = dateToSet.get("year");
    },

    // Every report kind goes through the archive: the backend generates, stores and answers with
    // the row, then the file is downloaded from the archive so what the user gets is exactly what
    // "Pobierz" will return later.
    generatePayload() {
      if (this.selectedReportType === DNR_BILLS) {
        return { kind: "DNR_BILLS", month: this.month, year: this.year };
      }
      if (this.isSqlReportSelected) {
        return { kind: "SQL", sql: this.selectedReportType };
      }
      return {
        kind: "STANDARD",
        type: this.selectedReportType,
        month: this.month,
        year: this.year,
      };
    },
    async generateReport() {
      if (this.generating) return;
      this.generating = true;
      try {
        const row = await this.$store.dispatch("generateArchivedReport", this.generatePayload());
        this.page = 1;
        await this.fetchArchive();
        // The row is already saved and refetched at this point - a failed download here means the
        // file transfer failed, not the archive save, so the success toast still fires only if the
        // download itself succeeded (download() already showed its own error toast otherwise).
        const downloaded = await this.download(row);
        if (downloaded) {
          this.$notify({ text: `Raport „${row.name}” zapisany w archiwum`, group: "success" });
        }
      } catch (error) {
        console.log(error);
        this.$notify({
          text: this.errorMessage(error, "Nie udało się wygenerować raportu"),
          group: "error",
        });
      } finally {
        this.generating = false;
      }
    },
    async download(report) {
      try {
        await this.$store.dispatch("downloadArchivedReport", {
          id: report.id,
          filename: report.filename,
        });
        return true;
      } catch (error) {
        console.log(error);
        this.$notify({
          text: this.errorMessage(error, "Nie udało się pobrać raportu"),
          group: "error",
        });
        return false;
      }
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },

    periodLabel(report) {
      if (!report.month || !report.year) return "-";
      const start = moment({ year: report.year, month: report.month - 1, day: 1 });
      return `${start.format("DD.MM.YYYY")} - ${start.clone().endOf("month").format("DD.MM.YYYY")}`;
    },
    formatDate(value) {
      return value ? moment(value).format("DD.MM.YYYY HH:mm") : "-";
    },

    async deleteReport() {
      if (confirm(` Czy na pewno chcesz usunąć raport?`)) {
        this.confirmDeleteReport();
      }
    },
    async confirmDeleteReport() {
      const id = this.sqlReports.find(
        (report) => report.query === this.selectedReportType
      )?.id;
      if (id) {
        const response = await this.$store.dispatch("deleteSqlReport", id);
        if (response) {
          this.selectedReportType = this.reportsOptions.length ? this.reportsOptions[0].value : "W1";
        }
      }
    },
  },
  computed: {
    archive() {
      return this.$store.getters.getReportArchive || { data: [], meta: null };
    },
    sqlReports() {
      return this.$store.getters.getDodSqlReports;
    },
    sqlReportsOptions() {
      if (this.sqlReports) {
        return this.sqlReports.map((r) => {
          return {
            shortName: r.title,
            value: r.query,
            name: r.title,
          };
        });
      } else {
        return [];
      }
    },
    allOptions() {
      return [...this.reportsOptions, ...this.sqlReportsOptions];
    },
    isSqlReportSelected() {
      return !this.reportsOptions.map((o) => o.value).includes(this.selectedReportType);
    },
    // SQL reports have no period; everything else is generated for the switched month.
    selectedNeedsMonth() {
      return !this.isSqlReportSelected;
    },
    reportsOptions() {
      const allReports = [
        {
          shortName: "Raport W1",
          name: "Raport W1",
          value: "W1",
        },
        {
          shortName: "Raport W2",
          name: "Raport W2",
          value: "W2",
        },
        {
          shortName: "Raport W3",
          name: "Raport W3",
          value: "W3",
        },
        {
          shortName: "Załącznik",
          name: "Załącznik",
          value: "ATTACHMENT",
        },
        {
          shortName: "Suma godzin wykonanych przez opiekunów",
          name: "Suma godzin wykonanych przez opiekunów",
          value: "TOTAL_IN_MONTH",
        },
        {
          shortName: "Suma godzin wykonanych MOPS (W1/W2/W3)",
          name: "Suma godzin wykonanych MOPS (W1/W2/W3)",
          value: "TOTAL_IN_MONTH_W1_W2_W3",
        },
        {
          shortName: "Suma godzin wykonanych PRIV DOD",
          name: "Suma godzin wykonanych PRIV DOD",
          value: "TOTAL_IN_MONTH_PRIV_DOD",
        },
        {
          shortName: "Rachunek DNR",
          name: "Rachunek DNR",
          value: DNR_BILLS,
        },
        {
          shortName: "Raport MOPS",
          name: "Raport MOPS",
          value: "MOPS",
        },
      ];
      const mopsValues = ["W1", "W2", "W3", "TOTAL_IN_MONTH_W1_W2_W3", "MOPS"];
      return allReports.filter((o) => {
        if (!this.panelCan("showMopsReports") && mopsValues.includes(o.value))
          return false;
        if (!this.panelCan("showBills") && o.value === DNR_BILLS) return false;
        return true;
      });
    },
  },
};
</script>

<style scoped>
table {
  table-layout: fixed;
  font-size: calc(11px + 0.05vw);
}
.generate-reports {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}
.month {
  margin: 0;
}
.action-buttons {
  display: flex;
  justify-content: space-between;
}
/* CustomSelect is shared across the panel, so the report picker is sized to match the buttons here only. */
.generate-reports >>> .main-part {
  height: 40px;
  width: 160px;
}
.generate-reports >>> .unfolded-part {
  top: 40px;
}
/* Global td rules force text-align and horizontal margins with !important; a span is not
   targeted by them, so it carries the right alignment. */
.cell-end {
  display: flex;
  justify-content: flex-end;
}
.cell-end .gradient-button {
  flex: none;
}
</style>
